/**
 * Guia — um capítulo por aba do menu: o que é, cada item/botão/opção, e o que dá para fazer.
 *
 * Os nomes entre aspas são os RÓTULOS da tela, letra por letra — é o que a pessoa procura com
 * os olhos. Mudou um rótulo na tela? Mude aqui no mesmo commit.
 */
import {
  AlertTriangle,
  BarChart3,
  CreditCard,
  FileText,
  History,
  LayoutDashboard,
  Megaphone,
  Radio,
  Settings,
  Shuffle,
  ShoppingBag,
  Target,
  Users,
} from 'lucide-react';

import { navItemById } from '../workspace/navigation';
import { img } from './imagens';
import type { CapituloDoGuia } from './tipos';

/** O resumo do menu (fonte única em `navigation.ts`) abre cada capítulo de aba. */
function resumoDoMenu(id: string, extra: string): string {
  const s = navItemById(id)?.summary;
  return s ? `${s} ${extra}` : extra;
}

/* ========================================================================== */

export const ABA_DASHBOARD: CapituloDoGuia = {
  id: 'dashboard',
  titulo: 'Dashboard',
  resumo: resumoDoMenu('dashboard', 'A tela que se abre 20 vezes por dia.'),
  icone: LayoutDashboard,
  secoes: [
    {
      id: 'topo',
      titulo: 'O topo: o dia em uma frase',
      passos: [
        {
          id: 'periodo',
          titulo: 'Período e atualização',
          texto:
            'O Dashboard abre sempre em "Hoje". Troque por 7, 30 ou 90 dias, ou toque em "Datas" para escolher de/até. Logo abaixo, duas linhas dizem quão fresco está o dado: "WhatsApp ao vivo · último evento há…" e "Meta atualizada há…". O ↻ ao lado de cada uma pede uma leitura agora e recarrega a tela quando o dado novo chega.',
          imagem: img('dashboard', 'Topo do Dashboard com o cartão violeta do dia', [
            'Período: Hoje, 7, 30, 90 dias ou Datas',
            'A frase do dia e o minigráfico de 00h a 23h',
            'O que fazer hoje',
            'Ficaram hoje, permanência, pessoas e grupos',
          ]),
        },
        {
          id: 'cartao-do-dia',
          titulo: 'O cartão violeta: "Olá, …"',
          texto:
            'Uma frase com quantas pessoas entraram hoje e quantas ficaram, comparada com ontem ATÉ A MESMA HORA (comparar com o dia inteiro de ontem faria toda manhã parecer um desastre). Embaixo: "Ficaram hoje", "Permanência do dia", "Pessoas nos grupos" e quantos grupos em quantas campanhas.',
        },
        {
          id: 'o-que-fazer',
          titulo: '"O que fazer hoje"',
          texto:
            'Só aparece o que pede ação, com o botão que resolve. Quando não há nada: "Nada pedindo sua atenção agora".',
          itens: [
            'N problemas nos seus grupos — "Ver e resolver" leva a Incidentes.',
            'Anúncios sem identificação — cliques chegando sem os parâmetros de URL. "Como corrigir" mostra o código para copiar; "Agora não" esconde por 30 dias neste navegador.',
            'Campanha sem custo — o bloco recebe gente, mas nenhuma campanha da Meta está ligada a ele. "Como corrigir" mostra o caminho (Campanhas › ⋯ › Meta Ads).',
            'Recomendações do dia — cada uma com a sua ação; dispense com "Já resolvi", "Não faz sentido" ou "Só dispensar".',
          ],
        },
      ],
    },
    {
      id: 'numeros',
      titulo: 'Os números do período',
      passos: [
        {
          id: 'evasao',
          titulo: 'Evasão',
          texto:
            'De quem entrou há 1, 3, 7, 15 dias… quantos saíram por conta própria. As colunas são os dias de entrada; toque numa coluna para ver aquele dia. Quem o administrador removeu aparece à parte — não é culpa do tráfego.',
        },
        {
          id: 'campanhas-no-periodo',
          titulo: 'Campanhas no período',
          texto:
            'Aparece quando houve gasto: orçamento de hoje, investimento, CPL Meta, CPL entrada, CPL real, entradas e taxa de entrada somados. "Ver por campanha" abre o detalhe em Campanhas.',
        },
        {
          id: 'indicadores',
          titulo: 'Saldo, entradas, saídas e taxa de entrada',
          texto:
            'Cada cartão traz a variação contra o período anterior e um (?) que explica como o número é medido. A barra "O que aconteceu com quem entrou" divide as entradas em ficaram, saíram e você removeu. Embaixo: visitas na página de ofertas, cliques na página, cliques e visitantes distintos.',
          imagem: img(
            'dashboard-indicadores',
            'Cartões de saldo, entradas, saídas e taxa de entrada',
            [
              'Saldo no período (o número de topo)',
              'Taxa de entrada',
              'Saídas: só quem saiu por conta própria',
            ],
          ),
        },
        {
          id: 'celular',
          titulo: 'No celular',
          texto:
            'O menu completo abre pelo botão do topo; a barra de baixo leva direto a Dashboard, Grupos, Incidentes, Monitoradores e Campanhas — as telas que se confere em pé, várias vezes por dia.',
          imagem: img('celular-menu', 'O menu do painel aberto no celular', [], {
            formato: 'celular',
          }),
        },
        {
          id: 'grafico',
          titulo: 'Entradas e saídas por dia',
          texto:
            'Colunas violeta são entradas, a linha âmbar são saídas — a mesma cor em todo o produto. Toque numa coluna para ver o dia; "Ver tabela" mostra os mesmos números em linhas.',
        },
        {
          id: 'funil',
          titulo: 'Do clique ao grupo',
          texto:
            'Os degraus do caminho: visitas na página → cliques → cliques únicos → entradas → ficaram. Toque num degrau para ler o que ele quer dizer e onde a perda acontece.',
          imagem: img('dashboard-funil', 'O funil "Do clique ao grupo"', ['Do clique ao grupo']),
        },
        {
          id: 'por-campanha',
          titulo: 'Entradas por campanha, lucro por lead, cobertura e permanência',
          texto:
            '"Entradas por campanha" mostra quem trouxe gente e quantos ficaram. "Lucro por lead" divide a comissão do dia pelas pessoas nos grupos — lance a comissão em "Lançar comissão do dia" (ou deixe a Shopee importar sozinha). "Cobertura de atribuição" diz de quantos cliques sabemos a origem. "Permanência" mostra a mediana de quanto tempo as pessoas ficam.',
        },
        {
          id: 'regiao',
          titulo: 'De onde é o seu público',
          texto:
            'Um mapa do Brasil por estado, montado pelo DDD do telefone de cada pessoa. Escolha "Nos grupos agora" ou "Entraram" no período, e filtre por campanha ou grupo. Ao lado: o estado principal, os DDDs mais comuns e a divisão por região. Quem só tem identificador do WhatsApp, sem telefone, entra como "sem DDD" — nunca num estado inventado.',
          imagem: img(
            'dashboard-regiao',
            'Cartão "De onde é o seu público" com o mapa por estado',
            [
              'Nos grupos agora × entraram no período',
              'O mapa: quanto mais escuro, mais gente',
              'Principais DDDs',
            ],
          ),
          notas: [
            {
              tom: 'dica',
              titulo: 'Use para o público do anúncio',
              texto:
                'Se 30% do grupo é de São Paulo e o anúncio roda no Brasil inteiro, talvez valha um conjunto só para SP. O mapa mostra onde o seu público já está.',
            },
          ],
        },
      ],
    },
  ],
};

/* ========================================================================== */

export const ABA_CAMPANHAS: CapituloDoGuia = {
  id: 'campanhas',
  titulo: 'Campanhas',
  resumo: resumoDoMenu('campanhas', 'É onde o gasto encontra as entradas.'),
  icone: Megaphone,
  secoes: [
    {
      id: 'visao',
      titulo: 'Desempenho: cards e árvore de custo',
      passos: [
        {
          id: 'cards',
          titulo: 'Cards: hoje, atualizado a cada minuto',
          texto:
            'Um cartão por campanha com código, situação, "Entradas hoje", minigráfico do dia, a barra de quem ficou e a lotação dos grupos. Campanha sem grupo mostra "Sem grupo vinculado" e o botão "Vincular grupo".',
          imagem: img('campanhas', 'Campanhas em cards', [
            'Nova campanha',
            'Cards × Árvore de custo',
            'Entradas de hoje de cada bloco',
          ]),
        },
        {
          id: 'arvore',
          titulo: 'Árvore de custo: "Desempenho das campanhas"',
          texto:
            'A tabela que responde "quanto custou cada pessoa". Escolha o período (com Datas) e leia por bloco; a seta abre as campanhas da Meta de cada bloco, cada uma com o próprio custo. A linha "Sem campanha da Meta identificada" junta cliques sem os parâmetros do anúncio — ela existe para a soma fechar com o total.',
          itens: [
            'Veiculação — se a campanha da Meta está no ar.',
            'Investimento — o gasto das campanhas da Meta ligadas ao bloco.',
            'Orçamento hoje — o orçamento diário na Meta. "Ajustar" muda o valor na própria Meta (só o Proprietário, em dois toques, com aviso acima de 20%).',
            'CPL Meta — gasto ÷ resultados que a Meta conta.',
            'CPL entrada — gasto ÷ pessoas que entraram vindas desses cliques.',
            'CPL real — gasto ÷ entradas, contando também quem entrou com o WhatsApp desconectado.',
            'Visitas na LP, Cliques no link e Página → clique — a nota da sua página de ofertas.',
            'Taxa de entrada — de cada 100 pessoas que clicaram, quantas entraram.',
            'Entradas, Evasão (entrou e saiu), Ficaram 24h, Evasão 1º dia e 7 dias, Custo/ficou 7d.',
          ],
          imagem: img('campanhas-desempenho', 'Tabela "Desempenho das campanhas"', [
            'Investimento',
            'CPL real',
            'Evasão 1º dia',
            'Abrir as campanhas da Meta do bloco',
          ]),
        },
      ],
    },
    {
      id: 'lista',
      titulo: 'Suas campanhas: criar, vincular, pausar',
      passos: [
        {
          id: 'nova',
          titulo: 'Nova campanha',
          texto:
            'Nome (o que você usa para falar dela: "Black Friday", "Ofertas de setembro") e descrição opcional. O código curto nasce sozinho e nunca muda, mesmo que você renomeie a campanha.',
          imagem: img('campanhas-lista', 'Tabela "Suas campanhas"', [
            'Código: a chave curta da campanha',
            'Grupos: vincular e desvincular',
            'Mais ações',
          ]),
        },
        {
          id: 'grupos-do-bloco',
          titulo: 'Grupos do bloco',
          texto:
            'O botão "Grupos" abre a gaveta com os grupos vinculados, na ordem da fila. Busque pelo nome, "Marcar os N encontrados" e "Vincular N grupos". É nessa ordem que o link enche os grupos.',
          imagem: img('campanha-grupos', 'Gaveta "Grupos de Ofertas de Outubro"', [
            'Posição na fila',
            'Desvincular',
          ]),
          notas: [
            {
              tom: 'sozinho',
              titulo: 'O convite é buscado sozinho',
              texto:
                'Ao vincular, o MGDLead busca o link de convite de cada grupo. Grupo sem convite não recebe ninguém — se faltar, Incidentes avisa.',
            },
          ],
        },
        {
          id: 'meta-do-bloco',
          titulo: '⋯ › Meta Ads: ligar a campanha da Meta ao bloco',
          texto:
            'A gaveta mostra as campanhas da Meta já ligadas e a lista para adicionar, com status e gasto de 7 dias. A que os cliques indicam aparece como "Sugerida". Marque e toque em "Vincular" — marcar sozinho não salva. Não está na lista? Informe o id da campanha.',
          imagem: img('campanha-meta', 'Gaveta "Meta Ads de Ofertas de Outubro"', [
            'Campanhas da Meta deste bloco',
            'Desvincular',
            'Adicionar campanhas da Meta',
          ]),
          notas: [
            {
              tom: 'atencao',
              titulo: 'Uma campanha da Meta fica em um bloco só',
              texto:
                'Assim o mesmo gasto nunca é contado duas vezes. Duplicou a campanha na Meta? Vincule a cópia também — senão as entradas dela ficam fora do custo, e o aviso amarelo "campanha da Meta sem bloco" aparece no topo de todas as telas.',
            },
          ],
        },
        {
          id: 'menu',
          titulo: 'O menu ⋯ de cada campanha',
          itens: [
            'Meta Ads — a gaveta acima.',
            'Editar — nome e descrição.',
            'Pausar / Retomar — pausada, o link para de mandar gente para os grupos dela.',
            'Arquivar — some da lista e libera a vaga do plano; "Mostrar arquivadas" traz de volta para desarquivar ou excluir.',
          ],
          texto:
            'Editar, pausar e arquivar pedem papel de Analista ou acima; arquivar e excluir, Administrador.',
        },
        {
          id: 'pagina',
          titulo: 'A página de uma campanha',
          texto:
            'Toque no nome para abrir: gráfico hora a hora (ou dia a dia) de cliques, entradas e saídas, os indicadores do bloco (visitas na LP, página → clique, ficaram 24h, evasão, vagas, lotação), a tabela por grupo e "Quanto do tráfego continua no grupo" — por marco, por anúncio, por campanha da Meta e por origem.',
          imagem: img('campanha-pagina', 'Página da campanha Ofertas de Outubro', [
            'Período',
            'Lotação do bloco',
            'Gráfico de cliques, entradas e saídas',
          ]),
        },
      ],
    },
  ],
};

/* ========================================================================== */

export const ABA_REDIRECIONADORES: CapituloDoGuia = {
  id: 'redirecionadores',
  titulo: 'Redirecionadores',
  resumo: resumoDoMenu('redirecionadores', 'O link que vai no botão "Entrar no grupo".'),
  icone: Shuffle,
  secoes: [
    {
      id: 'links',
      titulo: 'Seus links',
      passos: [
        {
          id: 'lista',
          titulo: 'A lista e os avisos',
          texto:
            'Cada link mostra campanha, cliques, origem e situação. Dois avisos podem aparecer no topo: "Um link tem grupo que nunca vai receber ninguém" (grupo sem convite — "Buscar convite" resolve) e "Um link está recebendo clique sem origem" (o botão da página não repassa os parâmetros — "Ver como arrumar").',
          imagem: img('redirecionadores', 'Tela de Redirecionadores', [
            'Novo redirecionador',
            'Destinos de reserva',
            'Instalação e cliques do link',
            'Origem dos cliques',
          ]),
        },
        {
          id: 'novo',
          titulo: 'Novo redirecionador: como repartir',
          texto: 'Nome, campanha e a forma de repartir as pessoas entre os grupos do bloco.',
          itens: [
            'Encher um grupo de cada vez — todo mundo vai para o mesmo grupo até ele bater o limite de segurança; aí passa para o próximo da fila. Grupo cheio conversa mais.',
            'Sortear entre os grupos — cada clique cai num grupo com vaga, sorteado. Os grupos crescem juntos.',
            'Mandar sempre para um grupo fixo — para testes ou um grupo VIP.',
            'Quando não houver vaga — mandar para um destino de reserva (um endereço) ou para um grupo de contingência. Assim o anúncio nunca aponta para o nada.',
          ],
        },
      ],
    },
    {
      id: 'gaveta',
      titulo: 'A gaveta do link',
      passos: [
        {
          id: 'abas',
          titulo: 'Instalação, Cliques, Quem clicou, Próximos grupos, Página',
          itens: [
            'Instalação — o trecho pronto para o botão e o teste.',
            'Cliques — cada clique, com filtros por origem e por situação.',
            'Quem clicou — o que aconteceu com quem clicou: entrou, ainda aguardando, não entrou; aparelhos e a divisão por região (DDD).',
            'Próximos grupos — para onde vão os próximos cliques e quanto cabe em cada grupo.',
            'Página — uma página de passagem antes do WhatsApp (título, imagem, pixel da Meta, GA4, GTM, Pinterest) e o teste A/B "com página × direto". Só Proprietário e Administrador.',
          ],
          texto: 'O botão "Instalação" na linha do link abre a gaveta.',
          imagem: img('redirecionador-quem-clicou', 'Aba "Quem clicou" de um link', [
            'Entrou ou não entrou',
            'A confiança do casamento clique → entrada',
            'Quanto tempo a pessoa ficou',
          ]),
        },
      ],
    },
  ],
};

/* ========================================================================== */

export const ABA_META: CapituloDoGuia = {
  id: 'meta-ads',
  titulo: 'Meta Ads',
  resumo: resumoDoMenu('meta-ads', 'Um espelho do que a Meta cobrou e entregou.'),
  icone: Target,
  secoes: [
    {
      id: 'conta',
      titulo: 'O cartão da conta',
      passos: [
        {
          id: 'cartao',
          titulo: 'Testar, sincronizar, trocar token',
          texto:
            'Cada conta conectada tem um selo de situação: "Sincronizando", "Aguardando a 1ª leitura", "Token vencido", "Erro na sincronização" ou "Sincronização pausada".',
          itens: [
            'Testar conexão — pergunta agora à Meta se o token funciona e mostra o status da conta.',
            'Sincronizar agora — uma leitura extra na hora (no máximo uma a cada 5 minutos).',
            'Trocar token — para token vencido ou revogado, sem desconectar e sem perder histórico (Administrador).',
            'Desconectar — para de ler a conta; o que já foi lido fica.',
          ],
          imagem: img('meta-ads', 'Tela de Meta Ads com a conta conectada', [
            'Testar conexão',
            'Sincronizar agora',
            'Trocar token',
            'Ver: total do período ou dia a dia',
          ]),
        },
      ],
    },
    {
      id: 'custo',
      titulo: 'Custo no período',
      passos: [
        {
          id: 'tabela',
          titulo: 'Campanhas, conjuntos e anúncios',
          texto:
            'Escolha o nível (Campanhas, Conjuntos, Anúncios), o período (7, 14 ou 28 dias) e "Ver: Total do período" (uma linha por campanha) ou "Dia a dia". Colunas: gasto, impressões, cliques no anúncio, custo por clique e dias.',
          imagem: img('meta-ads-tabela', 'Tabela de custo da Meta por campanha', [
            'Nível: campanhas, conjuntos, anúncios',
            'Gasto',
            'Para qual bloco a campanha manda gente',
          ]),
          notas: [
            {
              tom: 'atencao',
              titulo: '"Cliques no anúncio" não são os seus cliques',
              texto:
                'Quem conta é a Meta, e ela conta clique em qualquer lugar do anúncio — inclusive em quem desistiu antes de a página abrir. Os cliques no SEU link são outro número, contado pelo MGDLead.',
            },
          ],
        },
        {
          id: 'vinculo',
          titulo: '"→ bloco" e ocultar',
          texto:
            'Na linha de cada campanha: "→ bloco X · alterar" (ou "sem bloco · vincular") abre a janela para mover, desvincular ou vincular sem sair desta tela. O olho riscado esconde a campanha da tabela e dos totais — só para você, neste navegador; nada muda na Meta. "N ocultas · mostrar" traz de volta.',
        },
      ],
    },
  ],
};

/* ========================================================================== */

export const ABA_SHOPEE: CapituloDoGuia = {
  id: 'shopee',
  titulo: 'Shopee',
  resumo: resumoDoMenu('shopee', 'Para quem vende como afiliado.'),
  icone: ShoppingBag,
  secoes: [
    {
      id: 'tela',
      titulo: 'O que a tela mostra',
      passos: [
        {
          id: 'conexao',
          titulo: 'Conexão e importação',
          texto:
            'Depois de conectada (só o Proprietário), a comissão chega sozinha. "Importar agora" puxa na hora. "Trocar senha da API" e "Desconectar" ficam no cartão Conexão.',
        },
        {
          id: 'lucro',
          titulo: 'Lucro por lead por bloco e Sub IDs',
          texto:
            '"Lucro por lead por bloco" mostra comissão, pedidos e quanto cada pessoa do bloco rendeu. Em "Sub IDs", ligue cada Sub ID ao bloco que o usa no link de afiliado — é isso que leva a venda ao bloco certo.',
          notas: [
            {
              tom: 'dica',
              titulo: 'A régua do lucro por lead',
              texto:
                'As faixas (baixo, normal, ideal, muito bom) são calibradas no mercado de afiliado da Shopee e podem ser ajustadas por workspace.',
            },
          ],
        },
      ],
    },
  ],
};

/* ========================================================================== */

export const ABA_MONITORADORES: CapituloDoGuia = {
  id: 'monitoradores',
  titulo: 'Monitoradores',
  resumo: resumoDoMenu('monitoradores', 'Sem número conectado, nada é medido.'),
  icone: Radio,
  secoes: [
    {
      id: 'cartao',
      titulo: 'O cartão de cada número',
      passos: [
        {
          id: 'estado',
          titulo: 'Situação, grupos, último evento e captura',
          texto:
            'Cada número mostra se está Conectado, quantos grupos monitora, quando chegou o último evento e a captura (Capturando, Instável, Sem captura), além de há quanto tempo está conectado. O botão muda conforme o estado: "Conectar", "Ver QR" ou "Reconectar".',
          imagem: img('monitoradores', 'Cartões dos números conectados', [
            'Adicionar monitorador',
            'Situação',
            'Mais ações',
          ]),
          notas: [
            {
              tom: 'importante',
              titulo: 'Número caído é tráfego sem medição',
              texto:
                'Quando um número cai, uma faixa vermelha aparece em TODAS as telas com "Reconectar agora", e o aviso vai por e-mail e WhatsApp (Configurações › Avisos). O que acontece nos grupos enquanto ele está fora não volta depois.',
            },
          ],
        },
        {
          id: 'menu',
          titulo: 'Mais ações',
          itens: [
            'Ver detalhes — a faixa de atividade dos últimos 30 dias.',
            'Renomear — o nome do aparelho.',
            'Desconectar — tira o número do ar pelo painel (nunca pelo celular).',
            'Arquivar — o número sai de uso e libera a vaga do plano.',
          ],
          texto: 'Criar, conectar, arquivar e excluir são do Administrador ou do Proprietário.',
        },
        {
          id: 'reserva',
          titulo: 'Dois números no mesmo grupo: reserva',
          texto:
            'Com um segundo número dentro dos mesmos grupos, se o principal cair o reserva assume sozinho e nada se perde. Na lista de Grupos aparece o selo "2 números" — ou "sem reserva".',
        },
        {
          id: 'excluir',
          titulo: 'Excluir um número arquivado — o histórico fica',
          texto:
            'Em "Mostrar arquivados", o cartão do número arquivado tem "Excluir". Ele some de vez da lista, mas as entradas e saídas que ele registrou continuam contando nos relatórios: o histórico fica guardado.',
        },
      ],
    },
  ],
};

/* ========================================================================== */

export const ABA_GRUPOS: CapituloDoGuia = {
  id: 'grupos',
  titulo: 'Grupos',
  resumo: resumoDoMenu('grupos', 'E quem está dentro de cada um.'),
  icone: Users,
  secoes: [
    {
      id: 'lista',
      titulo: 'A lista de grupos',
      passos: [
        {
          id: 'tabela',
          titulo: 'Filtros, colunas e lotação',
          texto:
            'Abas "Todos", "Recebendo", "Cheios", "Pausados"; busca por nome ou ID; filtro de categoria. Cada linha mostra o grupo (com administradores × membros), o número que monitora, participantes, lotação, situação e a última varredura.',
          imagem: img('grupos', 'Tela de Grupos', [
            'Escolher grupos',
            'Filtros',
            'Sincronizar todos',
            'Lotação: contra o limite de 980, não 1.024',
          ]),
          notas: [
            {
              tom: 'dica',
              titulo: 'Por que 980?',
              texto:
                'O WhatsApp aceita 1.024, mas o link para de mandar gente perto de 980 para nunca mandar ninguém para grupo cheio. Ajuste por grupo em ⋯ › Limite de segurança.',
            },
          ],
        },
        {
          id: 'acoes',
          titulo: 'Ações',
          itens: [
            'Escolher grupos — a lista que vem do WhatsApp: busque, "Marcar os N encontrados", "Monitorar N grupos" (Administrador).',
            'Sincronizar todos / Sincronizar pessoas — refaz a varredura: quem está em cada grupo agora.',
            'Pausar — o grupo para de receber gente do link, mas segue medido.',
            'Categorias — etiquetas coloridas para organizar ("Moda", "Casa").',
            '⋯ › Linha do tempo — cada entrada e saída do grupo.',
            '⋯ › Limite de segurança — em quantas pessoas o link para de mandar gente.',
            '⋯ › Deixar de monitorar — o histórico fica; "Voltar a monitorar" desfaz.',
          ],
          texto: 'Marque vários grupos na caixa da linha para agir em lote.',
          imagem: img('celular-grupos', 'A lista de grupos no celular', [], {
            formato: 'celular',
            legenda: 'No celular a lista vira cartões, com a lotação de cada grupo à vista.',
          }),
        },
        {
          id: 'contatos',
          titulo: 'A aba Contatos: backup e listas prontas',
          texto:
            'Tudo de baixar contatos num lugar só. "Baixar backup completo" leva todo mundo que já passou pelos grupos; as listas prontas levam quem está (sem administradores) e quem saiu; "Montar lista" abre as opções completas. Embaixo ficam os últimos downloads — quem baixou, quando e o quê.',
          imagem: img('contatos', 'Aba Contatos', [
            'Backup completo',
            'Listas prontas',
            'Lista sob medida',
          ]),
        },
        {
          id: 'baixar',
          titulo: 'Baixar contatos e fazer backup',
          texto:
            'O caminho mais curto é a aba Contatos, no menu: "Baixar backup completo" (todo mundo que já passou pelos grupos), listas prontas de quem está e de quem saiu, "Montar lista" sob medida e os últimos downloads, com o lembrete de um backup por semana. Na lista de Grupos, "Baixar contatos" (Administrador) gera uma planilha com os contatos de vários grupos de uma vez — ou de todos. Antes de baixar, a tela já mostra quantas linhas, pessoas e telefones vão sair, com os filtros aplicados.',
          itens: [
            'Grupos — marque um a um ou todos de uma vez.',
            'Todos / Estão no grupo / Saíram — quem entra no arquivo.',
            'Sem administradores — tira os admins da lista.',
            'Quem está em 3 grupos sai em 3 linhas (uma por grupo, com o nome dele).',
          ],
          imagem: img('grupos-baixar-contatos', 'Janela "Baixar contatos"', [
            'Grupos para baixar',
            'Todos, estão no grupo ou saíram',
            'Sem administradores',
            'O que vai sair no arquivo',
          ]),
          notas: [
            {
              tom: 'atencao',
              titulo: 'Grupo nunca varrido sai quase vazio',
              texto:
                'Sabemos quantas pessoas estão lá, não quem são. Faça "Sincronizar pessoas" antes de baixar.',
            },
          ],
        },
      ],
    },
    {
      id: 'detalhe',
      titulo: 'A tela de um grupo',
      passos: [
        {
          id: 'composicao',
          titulo: 'Lotação, administradores × membros e convite',
          texto:
            'Toque no nome do grupo. Ali estão a lotação, a composição (participantes, administradores e membros — membros não inclui os administradores), o convite ("Com convite" / "Sem convite") com o histórico das checagens do link, e a linha do tempo.',
          imagem: img('grupo-detalhe', 'Tela do grupo Ofertas #05', [
            'Sincronizar pessoas',
            'Lotação',
            'Administradores × membros',
          ]),
        },
        {
          id: 'se-cair',
          titulo: '"Se este grupo cair"',
          texto:
            'Um seguro: "Baixar lista de contatos" gera a planilha de quem está no grupo, para você chamar as pessoas de volta se o grupo for derrubado.',
          imagem: img('grupo-contatos', 'Cartão "Se este grupo cair"', [
            'Pessoas e quantas têm telefone',
            'Baixar lista de contatos',
          ]),
        },
      ],
    },
  ],
};

/* ========================================================================== */

export const ABA_GESTAO: CapituloDoGuia = {
  id: 'gestao',
  titulo: 'Gestão',
  resumo: resumoDoMenu('analytics-gestao', 'Para decidir onde pôr a verba.'),
  icone: BarChart3,
  secoes: [
    {
      id: 'tela',
      titulo: 'Três tabelas, um recorte',
      passos: [
        {
          id: 'anuncio-ao-grupo',
          titulo: '"Do anúncio ao grupo"',
          texto:
            'A mesma árvore de custo de Campanhas: gasto, visitas, cliques, entradas, custo por entrada e custo por quem ficou, por bloco — e, abrindo a seta, por campanha da Meta.',
          imagem: img('gestao', 'Tela de Gestão', ['Período', 'Do anúncio ao grupo']),
        },
        {
          id: 'grupos-campanhas',
          titulo: 'Grupos e campanhas no período',
          itens: [
            'Grupos no período — pessoas, entradas, saídas, remoções, saldo e evasão de cada grupo.',
            'Campanhas no período — cliques, visitantes, de anúncio, sem origem, entradas casadas e taxa de entrada.',
          ],
          texto: 'Começa em 30 dias. Troque o período no topo para comparar.',
        },
      ],
    },
  ],
};

export const ABA_HISTORICO: CapituloDoGuia = {
  id: 'historico',
  titulo: 'Histórico',
  resumo: resumoDoMenu('analytics-historico', 'O ritmo da sua operação.'),
  icone: History,
  secoes: [
    {
      id: 'tela',
      titulo: 'O que tem aqui',
      passos: [
        {
          id: 'hora',
          titulo: 'Entradas e saídas por hora',
          texto:
            'A que horas as pessoas entram e saem — no fuso do workspace. Use para programar a verba e as postagens: anúncio rodando de madrugada com grupo parado é dinheiro na mesa.',
          imagem: img('historico', 'Entradas e saídas por hora', [
            'Entradas por hora',
            'Saídas por hora',
          ]),
        },
        {
          id: 'turma',
          titulo: 'Atividade do ano e evasão por turma',
          texto:
            '"Atividade do ano" mostra o movimento de cada dia. "Evasão por turma de entrada" compara as turmas (quem entrou no mesmo dia) em D0, D1, D7, D30…; "Evasão por origem" separa quem veio de anúncio, marcado à mão e sem origem.',
          imagem: img('historico-evasao', 'Evasão por turma de entrada', [
            'Evasão por turma de entrada',
          ]),
        },
      ],
    },
  ],
};

export const ABA_RELATORIOS: CapituloDoGuia = {
  id: 'relatorios',
  titulo: 'Relatórios',
  resumo: resumoDoMenu('analytics-relatorios', 'Para mandar ao cliente ou ao sócio.'),
  icone: FileText,
  secoes: [
    {
      id: 'tela',
      titulo: 'Baixar CSV ou PDF',
      passos: [
        {
          id: 'baixar',
          titulo: 'Escolha, confira, baixe',
          texto:
            'Escolha o período e a campanha (ou todas), confira em "O que vai sair no arquivo" — cada métrica com o valor e a confiança — e baixe em CSV (planilha) ou PDF. Pede papel de Analista ou acima.',
          imagem: img('relatorios', 'Tela de Relatórios', ['Campanha', 'Baixar CSV', 'Baixar PDF']),
        },
      ],
    },
  ],
};

export const ABA_INCIDENTES: CapituloDoGuia = {
  id: 'incidentes',
  titulo: 'Incidentes',
  resumo: resumoDoMenu('incidentes', 'O que o próprio sistema detectou de errado.'),
  icone: AlertTriangle,
  secoes: [
    {
      id: 'tela',
      titulo: 'Abrir, reconhecer, resolver',
      passos: [
        {
          id: 'lista',
          titulo: 'Cada incidente',
          texto:
            'Abas "Abertos", "Reconhecidos", "Resolvidos" e "Todos". Cada incidente traz a gravidade (Info, Atenção, Crítico), o que aconteceu e um atalho para o lugar do problema ("Abrir o grupo", "Abrir os monitoradores"…).',
          itens: [
            'Reconhecer — "eu vi e estou cuidando": o problema continua de pé, mas ganhou dono.',
            'Resolver — acabou. Se voltar a acontecer, um incidente novo abre.',
            'Convite quebrado — cole o novo link do grupo e "Validar e resolver".',
          ],
          imagem: img('incidentes', 'Tela de Incidentes', [
            'Abertos, reconhecidos, resolvidos',
            'Reconhecer',
            'Mais ações: resolver',
          ]),
          notas: [
            {
              tom: 'importante',
              titulo: '"Crítico" quer dizer dinheiro saindo agora',
              texto:
                'A gravidade sobe quando há campanha gastando no grupo ou link afetado. Grupo orgânico parado não vira crítico — é o funcionamento normal.',
            },
          ],
        },
        {
          id: 'tipos',
          titulo: 'Os avisos que podem aparecer',
          itens: [
            'Número fora do ar — o monitorador caiu.',
            'Grupo sem convite / convite revogado / convite de outro grupo — o link não consegue mandar gente.',
            'Link sem grupo disponível — tráfego caindo no vazio.',
            'Folga acabando — o bloco está ficando sem vaga; crie ou vincule um grupo.',
            'Token da Meta vencido — o gasto parou de chegar.',
            'Grupo sumiu — o grupo foi apagado ou o número saiu dele.',
            'Evasão alta / rajada de saídas — muita gente saindo de uma vez.',
            'Contagem divergente — o número do grupo não bate com quem sabemos que está lá.',
          ],
          texto: 'Ninguém abre incidente à mão: todos vêm da vigilância automática.',
        },
      ],
    },
  ],
};

export const ABA_ASSINATURA: CapituloDoGuia = {
  id: 'assinatura',
  titulo: 'Assinatura',
  resumo: resumoDoMenu('assinatura', 'E quando é hora de trocar de plano.'),
  icone: CreditCard,
  secoes: [
    {
      id: 'tela',
      titulo: 'Plano e consumo',
      passos: [
        {
          id: 'plano',
          titulo: 'Seu plano, consumo e troca',
          texto:
            '"Seu plano" mostra ciclo, teste grátis e vencimento. "Consumo do plano" mostra quanto de cada limite já está em uso (grupos, números, campanhas, links, membros, contas da Meta). Em "Trocar de plano", escolha o ciclo e o plano — só o Proprietário contrata.',
          imagem: img('assinatura', 'Tela de Assinatura', ['Consumo do plano']),
          notas: [
            {
              tom: 'dica',
              titulo: 'Arquivado não ocupa vaga',
              texto: 'Grupo, número ou campanha arquivados liberam a vaga do plano na hora.',
            },
          ],
        },
      ],
    },
  ],
};

export const ABA_CONFIGURACOES: CapituloDoGuia = {
  id: 'configuracoes',
  titulo: 'Configurações e equipe',
  resumo: resumoDoMenu('configuracoes', 'E quem mais tem acesso.'),
  icone: Settings,
  secoes: [
    {
      id: 'abas',
      titulo: 'As abas',
      passos: [
        {
          id: 'lista',
          titulo: 'Perfil, workspace, avisos, metas, aparência',
          itens: [
            'Perfil — o seu nome.',
            'Workspace — nome, endereço, fuso horário e idioma (Administrador).',
            'Avisos — e-mail e WhatsApp que recebem o aviso de número fora do ar; "Mandar mensagem de teste" confere.',
            'Metas — metas do mês por campanha: CPL real, evasão 1º dia e 7 dias, investimento diário e lucro por lead. Elas pintam o semáforo das telas.',
            'Plano — o mesmo que a tela Assinatura.',
            'Aparência — claro, escuro ou o do sistema.',
          ],
          texto: 'A aba fica no endereço: dá para mandar o link direto para alguém.',
          imagem: img('configuracoes', 'Configurações na aba Avisos', [
            'Abas',
            'E-mail para receber os avisos',
            'Mandar mensagem de teste',
          ]),
        },
        {
          id: 'escuro',
          titulo: 'Tema escuro',
          texto:
            'Para quem olha o painel à noite: Configurações › Aparência › Escuro, ou o botão de tema no topo.',
          imagem: img('escuro-dashboard', 'Dashboard no tema escuro'),
        },
      ],
    },
    {
      id: 'membros',
      titulo: 'Membros e papéis',
      passos: [
        {
          id: 'convidar',
          titulo: 'Convidar alguém',
          onde: 'Configurações › Membros',
          texto:
            'E-mail e papel, "Convidar". O convite fica em "Convites pendentes" até ser aceito (dá para revogar). Em "Pessoas com acesso", troque o papel pelo seletor ou remova.',
          imagem: img('membros', 'Tela de Membros', ['E-mail de quem convidar', 'Convidar']),
        },
        {
          id: 'papeis',
          titulo: 'Os quatro papéis',
          texto: 'Esconder um botão é só conforto: quem barra de verdade é o servidor.',
          blocos: [
            { rotulo: 'Membro', texto: 'Vê a operação inteira e não muda nada.' },
            {
              rotulo: 'Analista',
              texto:
                'Cuida do dia a dia: cria e edita campanhas e links, lança a comissão, reconhece e resolve incidentes, exporta relatórios.',
            },
            {
              rotulo: 'Administrador',
              texto:
                'Opera tudo, menos dinheiro e exclusão do workspace: números, grupos, membros, contatos (revelar telefone e baixar lista), conexões.',
            },
            {
              rotulo: 'Proprietário',
              texto:
                'Um só por workspace. Ajusta orçamento na Meta, conecta a Shopee, contrata o plano, transfere ou exclui o workspace.',
            },
          ],
        },
      ],
    },
  ],
};

export const CAPITULOS_DAS_ABAS: readonly CapituloDoGuia[] = [
  ABA_DASHBOARD,
  ABA_CAMPANHAS,
  ABA_REDIRECIONADORES,
  ABA_META,
  ABA_SHOPEE,
  ABA_MONITORADORES,
  ABA_GRUPOS,
  ABA_GESTAO,
  ABA_HISTORICO,
  ABA_RELATORIOS,
  ABA_INCIDENTES,
  ABA_ASSINATURA,
  ABA_CONFIGURACOES,
];
